"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { CheckCircle2, MessageCircle } from "@/components/icons";
import { SectionHeader } from "@/components/ui";
import { GREEN } from "@/lib/constants";

const MotionDiv = motion.div;

interface Props {
  competitorName: string;
  quickAnswer: string;
  takeaways: string[];
}

export default function AlternativeQuickAnswer({ competitorName, quickAnswer, takeaways }: Props) {
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = (delay = 0) => ({
    initial: prefersReducedMotion ? {} : { opacity: 0, y: 24 },
    whileInView: prefersReducedMotion ? {} : { opacity: 1, y: 0 },
    viewport: scrollRevealViewport,
    transition: { duration: 0.55, delay },
  });

  return (
    <section className="py-section border-border border-b" aria-labelledby="quick-answer-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv {...fadeUp(0)} className="mb-10 max-w-2xl">
          <SectionHeader
            eyebrow="Quick Answer"
            id="quick-answer-heading"
            heading={`Should you choose Proxidize or ${competitorName}?`}
            body="The short version, before the full breakdown below."
          />
        </MotionDiv>

        <MotionDiv
          {...fadeUp(0.08)}
          className="border-border bg-card mb-10 flex gap-4 rounded-xl border p-6 sm:p-7"
        >
          <MessageCircle
            size={20}
            className="mt-0.5 shrink-0"
            style={{ color: "var(--brand-green)" }}
            aria-hidden="true"
          />
          <p className="text-foreground text-sm leading-relaxed sm:text-base">{quickAnswer}</p>
        </MotionDiv>

        <MotionDiv
          {...fadeUp(0.16)}
          className="relative overflow-hidden rounded-2xl border p-8 sm:p-10"
          style={{
            borderColor: `${GREEN}26`,
            background: `linear-gradient(135deg, ${GREEN}0D 0%, transparent 60%)`,
          }}
        >
          <p className="text-muted-foreground mb-3 text-xs font-bold">Key Takeaways</p>
          <h3 className="text-foreground mb-6 text-2xl font-bold tracking-tight sm:text-3xl">
            What to remember comparing Proxidize and {competitorName}
          </h3>
          <ul className="grid list-none grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
            {takeaways.map((takeaway) => (
              <li
                key={takeaway}
                className="text-muted-foreground flex gap-3 text-sm leading-relaxed"
              >
                <CheckCircle2
                  size={17}
                  className="mt-0.5 shrink-0"
                  style={{ color: "var(--brand-green)" }}
                  aria-hidden="true"
                />
                {takeaway}
              </li>
            ))}
          </ul>
        </MotionDiv>
      </div>
    </section>
  );
}
